// HU-17 · Revisar, aprobar u observar el informe FAR-03 (spec `ui/MG-ILE-43.md`, tarea 17-5).
// Con HU-18 (rondas, `MG-ILE-44.md`) y HU-40 (constancias, `MG-ILE-65.md`).
//
// EL DETALLE DEL APROBADOR: la pestaña «Informe FAR-03» de la evaluación abierta desde la cola
// de aprobación. Lo que se lee es la VERSIÓN CONGELADA al enviar (`version_en_lectura`,
// HU-17-C10): un cambio posterior en la evaluación —el estado de una acción, que sigue abierto
// con el informe en revisión— no se ve aquí. Nunca se recompone el informe en la pantalla.
//
// LA PANTALLA DICE LA VERDAD (§8.1):
//   · «Aprobar» y «Devolver observado» salen de `acciones_disponibles` (HU-17-C9). La sesión
//     simulada de desarrollo llega al servidor con TODOS los roles, así que la pantalla además
//     exige estar en modo aprobador (`puedeDecidir`): la máscara solo resta, nunca agrega.
//   · La confirmación es `texto_de_confirmacion_de_aprobacion` («¿Aprobar el informe
//     PE-231-2026 v2?», C4) y lo que pasa con el caso, `texto_del_efecto_de_aprobar` (C6). La
//     pantalla no dice «Aprobar le da el código al informe»: el código ya existe (RN-07).
//   · El escalamiento es `texto_del_escalamiento` (HU-18-C5/C6), nunca un «4 de 3» armado aquí.
//   · Tras aprobar u observar se vuelve a la cola con el aviso de éxito; un 409 (otro aprobador
//     llegó antes, C11) muestra el texto del servidor y se relee el informe.
//
// El autor no viaja: lo pone el servidor desde la sesión (AT S3 §2.0, HU-40-C8).

import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, FileText, History, MessageSquareWarning, RefreshCw, ShieldCheck, ShieldX } from 'lucide-react';
import { Alert, Button, Card, DataTable, EmptyState, Field, Modal, Mono, StatusBadge, Tabs, Textarea } from './ui';
import type { ColumnaDeTabla, Pestana } from './ui';
import { aprobarInforme, observarInforme, ofrece, verInforme } from '../api/evaluacion';
import type { Informe, SeccionDelInforme } from '../api/evaluacion';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import { estadoDelInforme } from '../lib/etiquetas';
import ConstanciasDelInforme from './ConstanciasDelInforme';
import { ContenidoDeSoloLectura, useNombresDeLasAcciones } from './InformeFar03';

/** HU-17-C7: el texto bajo el campo cuando se intenta observar sin motivo. */
export const MENSAJE_SIN_MOTIVO = 'El motivo es obligatorio: es lo que el Comité corrige.';
/** El esquema HTTP (`ObservacionEntrada.motivo`, `max_length=2000`). */
const TOPE_DEL_MOTIVO = 2000;

/** Lo que vuelve a la cola tras decidir: el aviso de éxito y, si se aprobó, el caso para «Ver expediente». */
export type DecisionDelAprobador = { texto: string; casoId: string; aprobado: boolean };

/**
 * Las secciones que lee el aprobador. Con el informe en revisión, las de la versión congelada
 * (C10); en cualquier otro estado el servidor no la manda y se leen las del informe tal cual.
 */
const seccionesParaLeer = (informe: Informe): SeccionDelInforme[] => {
  const congeladas = informe.version_en_lectura?.secciones;
  if (!congeladas) return informe.secciones;
  return congeladas.map((s, i) => ({
    numero: s.numero ?? i + 1,
    clave: s.clave ?? `seccion-${i + 1}`,
    titulo: s.titulo ?? '',
    texto: s.texto ?? null,
    editable: false,
    nota: null,
    filas: Array.isArray(s.filas) ? s.filas : [],
  }));
};

const COLUMNAS_DE_RONDAS: ColumnaDeTabla[] = [
  { key: 'ronda', label: 'Ronda', className: 'w-24' },
  { key: 'texto', label: 'Detalle' },
];

/** HU-17-C7 / C6: la tarjeta «Aprobación». Solo se pinta si el servidor ofrece aprobar u observar. */
function TarjetaDeAprobacion({ informe, onDecidido, onReleer, onFalloDeEnvio }: Readonly<{
  informe: Informe;
  onDecidido: (d: DecisionDelAprobador) => void;
  onReleer: () => void;
  /** El fallo lo pinta el padre: tras un 409 se relee y esta tarjeta desaparece con el informe ya decidido. */
  onFalloDeEnvio: (f: Fallo | null) => void;
}>) {
  const [confirmando, setConfirmando] = useState(false);
  const [observando, setObservando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [errorDelMotivo, setErrorDelMotivo] = useState<string | null>(null);
  const setFalloDeEnvio = onFalloDeEnvio;
  const [ocupado, setOcupado] = useState(false);
  const codigo = informe.codigo ?? '';

  const aprobar = async () => {
    setFalloDeEnvio(null);
    setOcupado(true);
    try {
      const aprobado = await aprobarInforme(informe.caso_id);
      setConfirmando(false);
      onDecidido({ texto: `Informe ${aprobado.codigo ?? codigo} v${aprobado.version} aprobado`, casoId: informe.caso_id, aprobado: true });
    } catch (e) {
      // HU-17-C11: otro aprobador llegó antes. El texto es el del servidor y se relee: el
      // informe ya no está en revisión y la tarjeta desaparece sola.
      setConfirmando(false);
      setFalloDeEnvio(aFallo(e));
      onReleer();
    } finally {
      setOcupado(false);
    }
  };

  const observar = async () => {
    // HU-17-C7: sin motivo no se envía; es lo que el Comité lee para subsanar.
    if (!motivo.trim()) { setErrorDelMotivo(MENSAJE_SIN_MOTIVO); return; }
    setErrorDelMotivo(null);
    setFalloDeEnvio(null);
    setOcupado(true);
    try {
      const observado = await observarInforme(informe.caso_id, motivo.trim());
      onDecidido({
        texto: `Informe ${observado.codigo ?? codigo} v${observado.version} devuelto al Comité (ronda ${observado.iteraciones})`,
        casoId: informe.caso_id,
        aprobado: false,
      });
    } catch (e) {
      const f = aFallo(e);
      // El 422 del motivo va bajo el campo, con el texto del servidor; lo demás, arriba.
      if (f.estado === 422) setErrorDelMotivo(f.message);
      else { setFalloDeEnvio(f); onReleer(); }
    } finally {
      setOcupado(false);
    }
  };

  return (
    <Card data-testid="aprobacion-tarjeta" title="Aprobación" icon={CheckCircle2} subtitle={`Versión ${informe.version} en revisión.`}>
      {observando && (
        <div className="mb-3">
          <Field label="Motivo de la observación" required error={errorDelMotivo}
            hint="Llega al Comité tal cual y queda en el historial del informe."
            contador={`${motivo.length} / ${TOPE_DEL_MOTIVO} caracteres`}>
            <Textarea data-testid="aprobacion-form-motivo" rows={3} value={motivo} maxLength={TOPE_DEL_MOTIVO}
              onChange={(e) => { setMotivo(e.target.value); setErrorDelMotivo(null); }} />
          </Field>
        </div>
      )}
      {/* Primario a la derecha, secundario a su izquierda (lineamiento, patrones de formulario). */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {observando ? (
          <>
            <Button variant="ghost" onClick={() => { setObservando(false); setMotivo(''); setErrorDelMotivo(null); }} disabled={ocupado}>Cancelar</Button>
            <Button data-testid="aprobacion-form-enviar-observacion" variant="danger" icon={MessageSquareWarning}
              onClick={() => { observar(); }} disabled={ocupado}>
              {ocupado ? 'Enviando…' : 'Devolver observado'}
            </Button>
          </>
        ) : (
          ofrece(informe, 'observar') && (
            <Button data-testid="aprobacion-observar" variant="secondary" icon={MessageSquareWarning}
              onClick={() => setObservando(true)} disabled={ocupado}>
              Devolver observado
            </Button>
          )
        )}
        {ofrece(informe, 'aprobar') && !observando && (
          <Button data-testid="aprobacion-aprobar" icon={CheckCircle2} onClick={() => setConfirmando(true)} disabled={ocupado}>
            Aprobar el informe v{informe.version}
          </Button>
        )}
      </div>
      <Modal data-testid="aprobacion-confirmar-aprobar" open={confirmando} onClose={() => setConfirmando(false)} tone="warning" icon={CheckCircle2}
        eyebrow="Aprobación · Informe FAR-03"
        title={informe.texto_de_confirmacion_de_aprobacion ?? `¿Aprobar el informe v${informe.version}?`}
        footer={<>
          <Button data-testid="aprobacion-cancelar-aprobar" variant="secondary" onClick={() => setConfirmando(false)}>Cancelar</Button>
          <Button icon={CheckCircle2} onClick={() => { aprobar(); }} disabled={ocupado}>{ocupado ? 'Aprobando…' : 'Sí, aprobar'}</Button>
        </>}>
        {informe.texto_del_efecto_de_aprobar && <p className="font-medium">{informe.texto_del_efecto_de_aprobar}</p>}
        <p>Después de aprobado no se edita: un cambio exige una versión nueva.</p>
      </Modal>
    </Card>
  );
}

export default function AprobacionDelInforme({ casoId, zonaHoraria, puedeDecidir, onDecidido, onVolver }: Readonly<{
  casoId: string;
  /** RN-82: la zona del país del caso (`useZonaDelPais`), en la que se leen las horas; `null` = Lima. */
  zonaHoraria: string | null;
  /** La sesión entró como aprobador (desde la cola). Sin esto no se ofrece decidir aunque el servidor lo permitiera. */
  puedeDecidir: boolean;
  onDecidido: (d: DecisionDelAprobador) => void;
  /** «Volver» del estado sin permiso: la salida a su bandeja. */
  onVolver?: () => void;
}>) {
  const [informe, setInforme] = useState<Informe | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [cargando, setCargando] = useState(true);
  const [pestana, setPestana] = useState('rondas');
  // HU-17-C11: el 409 de otro aprobador (o cualquier fallo al decidir), con el texto del servidor.
  const [falloDeEnvio, setFalloDeEnvio] = useState<Fallo | null>(null);
  const nombreDe = useNombresDeLasAcciones();

  const leer = useCallback(async () => {
    setCargando(true);
    setFallo(null);
    try {
      setInforme(await verInforme(casoId));
    } catch (e) {
      setFallo(aFallo(e));
    } finally {
      setCargando(false);
    }
  }, [casoId]);

  useEffect(() => { leer(); }, [leer]);

  if (cargando && !informe) return <p data-testid="aprobacion-cargando" className="p-6 text-body text-ink-soft">Abriendo el informe…</p>;

  if (fallo && !informe) {
    if (fallo.estado === 403) {
      return (
        <EmptyState data-testid="aprobacion-sin-permiso" icon={ShieldX}
          title="Solo la Vicepresidencia y la Gerencia General aprueban el informe" description={fallo.message}
          action={onVolver && <Button variant="secondary" onClick={onVolver}>Volver a la bandeja</Button>} />
      );
    }
    if (fallo.estado === 404) {
      return <p className="p-6 text-body text-ink-soft">El caso todavía no tiene informe elaborado.</p>;
    }
    return (
      <Alert data-testid="aprobacion-error" tone="danger" title="No se pudo abrir el informe" detalleTecnico={fallo.detalleTecnico}>
        {fallo.message}{fallo.estado === 0 && ' Revisa tu conexión e intenta refrescar.'}
        <div className="mt-2"><Button size="sm" variant="secondary" icon={RefreshCw} onClick={() => { leer(); }}>Reintentar</Button></div>
      </Alert>
    );
  }
  if (!informe) return null;

  // R1: el texto del servidor (`informe.texto_del_estado`); el glosario da el tono y cubre un
  // servidor anterior que no lo trae.
  const estado = { ...estadoDelInforme(informe.estado), ...(informe.texto_del_estado ? { texto: informe.texto_del_estado } : {}) };
  const decide = puedeDecidir && (ofrece(informe, 'aprobar') || ofrece(informe, 'observar'));
  const rondas = informe.rondas ?? [];
  const constancias = informe.constancias ?? [];
  const pestanas: Pestana[] = [
    { key: 'rondas', label: 'Rondas', icon: History, count: rondas.length, testid: 'aprobacion-tab-rondas' },
    { key: 'constancias', label: 'Constancias', icon: ShieldCheck, count: constancias.length, testid: 'aprobacion-tab-constancias' },
  ];

  // Disposición de la maqueta del AF (§12.1, Figura 12): el informe a la izquierda y la decisión a
  // la derecha, fija al desplazarse; en pantallas angostas la decisión va primero, para no
  // tener que recorrer las ocho secciones hasta encontrar «Aprobar». El historial, debajo, a
  // todo lo ancho: la tabla de constancias no cabe en la columna lateral.
  return (
    <div className="space-y-4">
      {/* Sin decisión que tomar (Comité, administrador, ya decidido) no se reserva la columna. */}
      <div className={'grid grid-cols-1 gap-4 items-start' + (decide || falloDeEnvio ? ' lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]' : '')}>
        <div className="space-y-4 lg:order-2 lg:sticky lg:top-4">
          {falloDeEnvio && (
            <Alert data-testid="aprobacion-error-envio" tone={falloDeEnvio.estado === 409 ? 'warning' : 'danger'}
              title="No se pudo registrar la decisión" detalleTecnico={falloDeEnvio.detalleTecnico} onCerrar={() => setFalloDeEnvio(null)}>
              {falloDeEnvio.message}
            </Alert>
          )}
          {decide && <TarjetaDeAprobacion informe={informe} onDecidido={onDecidido} onReleer={() => { leer(); }} onFalloDeEnvio={setFalloDeEnvio} />}
        </div>
        <Card className="lg:order-1" icon={FileText}
          title={<>Informe FAR-03 · <span data-testid="aprobacion-codigo"><Mono className="font-semibold">{informe.codigo ?? ''}</Mono></span></>}
          actions={
            <span className="inline-flex flex-wrap items-center gap-2">
              <StatusBadge data-testid="aprobacion-version" tone="neutral" dot={false}>v{informe.version}</StatusBadge>
              <StatusBadge data-testid="aprobacion-ronda" tone="neutral" dot={false}>Ronda {informe.iteraciones}</StatusBadge>
              <StatusBadge data-testid="aprobacion-estado" tone={estado.tono}>{estado.texto}</StatusBadge>
            </span>
          }>
          {informe.texto_del_escalamiento && (
            <div className="mb-3">
              <Alert data-testid="aprobacion-escalamiento" tone={informe.escalado_a_gerencia ? 'warning' : 'info'} icon={AlertTriangle}
                title={informe.texto_del_escalamiento} />
            </div>
          )}
          <div className="space-y-3" data-testid="aprobacion-secciones" aria-label="Secciones del FAR-03">
            {seccionesParaLeer(informe).map((s) => (
              <section key={s.clave} aria-label={`${s.numero}. ${s.titulo}`} className="border border-hairline rounded-xl p-4">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <Mono className="text-meta">{s.numero}</Mono>
                  <h3 className="text-body font-semibold text-ink">{s.titulo}</h3>
                </div>
                <ContenidoDeSoloLectura seccion={s} nombreDe={nombreDe} />
              </section>
            ))}
          </div>
        </Card>
      </div>

      <Card pad={false}>
        <div className="px-5 pt-2">
          <Tabs tabs={pestanas} active={pestana} onChange={setPestana} aria-label="Historial del informe">
            <div className="py-4">
              {pestana === 'rondas' && (
                <div data-testid="aprobacion-rondas">
                  <DataTable sinMarco columns={COLUMNAS_DE_RONDAS}
                    rows={rondas.map((r) => ({ id: r.ronda, ronda: r.ronda, texto: r.texto }))}
                    renderCell={(fila, columna) => (columna.key === 'ronda' ? <Mono>{String(fila.ronda)}</Mono> : String(fila.texto))}
                    empty={<EmptyState icon={History} title="Sin rondas de observación" />} />
                </div>
              )}
              {pestana === 'constancias' && (
                <div data-testid="aprobacion-constancias">
                  <ConstanciasDelInforme constancias={constancias} zonaHoraria={zonaHoraria} />
                </div>
              )}
            </div>
          </Tabs>
        </div>
      </Card>
    </div>
  );
}
